import { useEffect, useRef, useState, type FormEvent, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { fastapi } from '../services/api';
import { useGetBotsQuery } from '../store/bots/botsApi';
import BtnTaratic from '../components/ui/BtnTaratic';
import { GhostSelect } from '../components/ui/GhostSelect';
import StatusNotice from '../components/ui/StatusNotice';
import StatusLabel from '../components/ui/StatusLabel';
import BehaviorInfo from '../components/ui/BehaviorInfo';
import DefaultCard from '../components/ui/DefaultCard';
import './settings/workflows.css';
import './post-bot.css';

type Settings = { enabled: boolean; bot_id: number | null; instructions: string; public_information: string };
type Override = Settings & { customer_id: string };
type Customer = { id: string; customer_id: string | null; name: string; username?: string };
type Activity = { account_id: string; comment_id: string; customer_id: string; comment_text: string; status: string; reply: string; created_at: string };
const blank = (): Settings => ({ enabled: false, bot_id: null, instructions: '', public_information: '' });
const statuses: Record<string, string> = { sent: 'Publicado', skipped_negative: 'Omitido: negativo', skipped_unsafe: 'Omitido: seguridad', failed: 'Error', uncertain: 'Sin confirmar', processing: 'En proceso', cancelled: 'Cancelado', rate_limited: 'Limitado por Instagram' };

export default function PostBotPage() {
  const { data: bots = [], isLoading: botsLoading, isError: botsError } = useGetBotsQuery();
  const [general, setGeneral] = useState<Settings>(blank);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [draft, setDraft] = useState<Settings>(blank);
  const customerHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (customer) customerHeading.current?.focus(); }, [customer]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [settings, history] = await Promise.all([fastapi.get('/post-bot'), fastapi.get('/post-bot/activity')]);
      setGeneral(settings.data.general); setOverrides(settings.data.overrides); setActivity(history.data); setLoaded(true);
    } catch { setError('No se pudo cargar el bot de publicaciones.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!query.trim()) { setCustomers([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void fastapi.get('/post-bot/customers/search', { params: { q: query.trim() } })
        .then(({ data }) => { if (!cancelled) setCustomers(data.items); })
        .catch(() => { if (!cancelled) { setCustomers([]); setError('No se pudieron buscar los clientes.'); } })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query]);

  const selectCustomer = (item: Customer) => {
    const id = item.customer_id || item.id;
    setCustomer({ ...item, customer_id: id });
    const existing = overrides.find(o => o.customer_id === id);
    setDraft(existing ? { enabled: existing.enabled, bot_id: existing.bot_id, instructions: existing.instructions, public_information: existing.public_information }
      : { ...blank(), bot_id: general.bot_id });
    setQuery(''); setCustomers([]); setNotice('');
  };
  const save = async (event: FormEvent, individual: boolean) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    const value = individual ? draft : general;
    try {
      const { data } = await fastapi.put(individual ? `/post-bot/customers/${customer!.customer_id}` : '/post-bot', value);
      if (individual) setOverrides(current => [...current.filter(o => o.customer_id !== customer!.customer_id), { ...data, customer_id: customer!.customer_id }]);
      else setGeneral(data);
      setNotice(individual ? 'Configuración del cliente guardada.' : 'Bot de publicaciones guardado.');
    } catch { setError('No se pudo guardar. Comprueba el bot seleccionado e inténtalo de nuevo.'); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!customer) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await fastapi.delete(`/post-bot/customers/${customer.customer_id}`);
      setOverrides(current => current.filter(o => o.customer_id !== customer.customer_id));
      setCustomer(null); setNotice('El cliente vuelve a usar el bot general.');
    } catch { setError('No se pudo restablecer la configuración.'); }
    finally { setBusy(false); }
  };
  const fields = (value: Settings, update: Dispatch<SetStateAction<Settings>>, individual: boolean) => <>
    <label className="workflow-activation"><input type="checkbox" checked={value.enabled} onChange={e => update({ ...value, enabled: e.target.checked })} />
      <span>{individual ? 'Responder a este cliente' : 'Activar respuestas públicas'}<small>{individual ? 'Desactivado: omitir todos sus comentarios.' : 'La pausa general detiene también las configuraciones por cliente.'}</small></span>
    </label>
    <div className="workflow-field"><span>Bot {individual ? 'del cliente' : 'general'}</span>
      <GhostSelect ariaLabel={individual ? 'Bot del cliente' : 'Bot general'} value={String(value.bot_id || '')}
        options={bots.map(bot => ({ value: String(bot.id), label: bot.name }))} placeholder="Seleccionar bot"
        disabled={busy || botsLoading || botsError} onChange={id => update({ ...value, bot_id: Number(id) || null })} />
    </div>
    <label>{individual ? 'Instrucciones adicionales del cliente' : 'Instrucciones para respuestas públicas'}
      <textarea maxLength={6000} value={value.instructions} onChange={e => update({ ...value, instructions: e.target.value })} placeholder="Tono, estilo y forma de responder…" /></label>
    {!individual && <label>Información que se puede publicar
      <textarea maxLength={12000} value={value.public_information} onChange={e => update({ ...value, public_information: e.target.value })} placeholder="Horarios, servicios e información general del negocio. No incluyas datos de clientes." /></label>}
    <BtnTaratic color="green" type="submit" loading={busy} disabled={botsError || (value.enabled && !value.bot_id)}>{individual ? 'Guardar cliente' : 'Guardar bot general'}</BtnTaratic>
  </>;

  return <div className="workflows-page post-bot-page page-theme-surface">
    <header className="workflows-header"><div><h1>Bot de publicaciones</h1><p>Respuestas públicas en tus posts y reels de Instagram.</p></div>
      <BehaviorInfo label="Cómo responde el bot de publicaciones" title="Respuestas públicas con contexto privado">
        <p>El bot consulta las notas y mensajes almacenados de cada cliente. Los historiales largos se resumen por bloques; no se consulta el historial de Meta.</p>
        <p>Los comentarios negativos, las quejas, los insultos y los casos dudosos se omiten. Una segunda revisión comprueba que la respuesta no publique información privada. Estas reglas no se pueden desactivar.</p>
        <p>El bot no ejecuta acciones del chat. Las configuraciones de DM y los workflows de mensajes privados siguen siendo independientes. La clasificación automática puede equivocarse.</p>
      </BehaviorInfo></header>
    {error && <StatusNotice variant="danger">{error}</StatusNotice>}
    {notice && <StatusNotice variant="success">{notice}</StatusNotice>}
    {botsError && <StatusNotice variant="danger">No se pudo cargar la lista de bots.</StatusNotice>}
    {loading ? <p role="status">Cargando…</p> : !loaded ? <BtnTaratic onClick={() => void load()}>Reintentar</BtnTaratic> : <>
      <div className="workflows-grid">
        <DefaultCard as="section" className="workflow-card"><h2>Bot general</h2><p>Define quién responde y qué información del negocio puede compartir.</p>
          {!botsLoading && !botsError && bots.length === 0 && <p>Crea primero un bot en <Link to="/bots-config">Configuración de bots</Link>.</p>}
          <form className="workflow-editor" onSubmit={e => void save(e, false)}><fieldset disabled={busy}>{fields(general, setGeneral, false)}</fieldset></form>
        </DefaultCard>
        <DefaultCard as="section" className="workflow-card"><h2>Configuración por cliente</h2><p>Elige otro bot o pausa las respuestas para un cliente, sin cambiar sus DM.</p>
          <label className="post-bot-search">Buscar cliente<input disabled={busy} value={query} onChange={e => setQuery(e.target.value)} placeholder="Nombre, identificador o comentario" /></label>
          {searching && <p role="status">Buscando…</p>}
          {!searching && query.trim() && customers.length === 0 && <p>No se encontraron clientes.</p>}
          <div className="post-bot-customer-list">{customers.map(item => <BtnTaratic key={item.id} disabled={busy} onClick={() => selectCustomer(item)}>{item.name}{item.username ? ` (@${item.username})` : ''}</BtnTaratic>)}</div>
          {customer && <form className="workflow-editor" onSubmit={e => void save(e, true)}><h3 tabIndex={-1} ref={customerHeading}>{customer.name}</h3><fieldset disabled={busy}>
            {fields(draft, setDraft, true)}<BtnTaratic type="button" onClick={() => void reset()}>Usar configuración general</BtnTaratic>
          </fieldset></form>}
          <div className="post-bot-customer-list">{overrides.map(item => <BtnTaratic key={item.customer_id} disabled={busy} onClick={() => selectCustomer({ id: item.customer_id, customer_id: item.customer_id, name: item.customer_id })}>
            {item.customer_id} · {item.enabled ? bots.find(b => b.id === item.bot_id)?.name || 'Bot no disponible' : 'Pausado'}
          </BtnTaratic>)}</div>
          {!overrides.length && <p>Sin excepciones: se aplica la configuración general.</p>}
        </DefaultCard>
      </div>
      <DefaultCard as="section" className="workflow-card"><div className="workflow-card-heading"><h2>Actividad reciente</h2><BtnTaratic disabled={busy} onClick={() => {
        setBusy(true); setError(''); void fastapi.get('/post-bot/activity').then(({ data }) => setActivity(data))
          .catch(() => setError('No se pudo actualizar la actividad.')).finally(() => setBusy(false));
      }}>Actualizar actividad</BtnTaratic></div>
        {!activity.length && <p>Aquí aparecerán las respuestas publicadas y los comentarios omitidos.</p>}
        {activity.map(item => <article className="post-bot-activity" key={`${item.account_id}:${item.comment_id}`}>
          <div><span>Cliente {item.customer_id}</span><StatusLabel variant={item.status === 'sent' ? 'success' : item.status === 'failed' ? 'danger' : 'neutral'}>{statuses[item.status] || item.status}</StatusLabel></div>
          <p>{item.comment_text}</p>{item.reply && <p>Respuesta: {item.reply}</p>}<time dateTime={item.created_at}>{new Date(item.created_at).toLocaleString()}</time>
          <BtnTaratic disabled={busy} onClick={() => selectCustomer({ id: item.customer_id, customer_id: item.customer_id, name: item.customer_id })}>Configurar cliente {item.customer_id}</BtnTaratic>
        </article>)}
      </DefaultCard>
    </>}
  </div>;
}
